import React, { useState, useEffect } from 'react';
import { losApi } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { isSpendApprovalError } from '../../api';
import { Loader2, FileText, FileCheck2 } from 'lucide-react';
import { ModuleHeader, Fld } from '../shared/formFields';
import { copyText } from '../../clipboard';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { formatDateTime } from '../../kit/time';

const DISCLOSURE_FORM_CATALOG = [
  { key: 'econsent', label: 'eConsent Agreement', default: true },
  { key: 'credit_auth', label: 'Credit Authorization', default: true },
  { key: 'ecoa', label: 'Equal Credit Opportunity Act (ECOA) Notice', default: true },
  { key: 'privacy', label: 'Privacy Policy', default: true },
  { key: 'servicing_disclosure', label: 'Servicing Disclosure Statement', default: true },
  { key: 'right_to_copy_appraisal', label: 'Right to Receive Copy of Appraisal', default: true },
  { key: 'anti_steering', label: 'Anti-Steering Loan Options Disclosure', default: true },
  { key: 'intent_to_proceed', label: 'Intent to Proceed', default: true },
  { key: 'patriot_act', label: 'USA PATRIOT Act Customer Identification', default: true },
];

const EMAIL_FAILURE = {
  provider_not_configured: 'Email is not set up for this account.',
  frontend_url_not_set: 'The site address is not configured, so no link could be built.',
  send_failed: 'The email service did not accept the message.',
  no_signer_email: 'There is no signer email on this request.',
  invalid_address: 'The signer email is not a valid single address.',
  recipient_check_failed: 'Could not check the address just now. Try again in a minute.',
};
const emailFailure = (reason) => (String(reason || '').startsWith('recipient_blocked')
  ? 'That address has opted out of email or bounced.'
  : EMAIL_FAILURE[reason] || 'The email did not go out.');

export function DisclosureFormsModule({ loan }) {
  const b = loan.urla?.borrower || {};
  const defaultEmail = b.email || '';
  const defaultName = [b.first_name, b.last_name].filter(Boolean).join(' ') || 'Borrower';
  const [selected, setSelected] = useState(() => new Set(DISCLOSURE_FORM_CATALOG.filter(f => f.default).map(f => f.key)));
  const [signerName, setSignerName] = useState(defaultName);
  const [signerEmail, setSignerEmail] = useState(defaultEmail);
  const [sending, setSending] = useState(false);
  const [requests, setRequests] = useState([]);
  useEffect(() => { losApi.listSignatureRequests(loan.loan_file_id).then(setRequests); }, [loan.loan_file_id]);
  useEffect(() => { setSignerName(defaultName); setSignerEmail(defaultEmail); }, [defaultName, defaultEmail]);

  const toggle = (k) => {
    const s = new Set(selected);
    s.has(k) ? s.delete(k) : s.add(k);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === DISCLOSURE_FORM_CATALOG.length) setSelected(new Set());
    else setSelected(new Set(DISCLOSURE_FORM_CATALOG.map(f => f.key)));
  };

  const doSend = async () => {
    if (!signerEmail) { toast({ title: 'Signer email required' }); return; }
    if (selected.size === 0) { toast({ title: 'Select at least one form' }); return; }
    const ok = await confirmDialog({
      title: 'Email the signing link?',
      description: `${signerName || 'The signer'} (${signerEmail}) gets an email with a link to review and sign ${selected.size} form${selected.size !== 1 ? 's' : ''}.`,
      confirmText: 'Send email',
    });
    if (!ok) return;
    setSending(true);
    try {
      const res = await losApi.sendDisclosurePackage(loan.loan_file_id, {
        forms: Array.from(selected),
        signer_name: signerName,
        signer_email: signerEmail,
      });
      if (res.email_sent) {
        toast({ title: 'Signing link emailed', description: `Sent to ${res.sent_to}` });
      } else {
        toast({ title: 'Package created, but the email did not go out', description: `${emailFailure(res.reason)} Use Copy Link below.` });
      }
      window.open(losApi.publicPdfUrl(res.public_token), '_blank');
      const list = await losApi.listSignatureRequests(loan.loan_file_id);
      setRequests(list);
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'Send failed', description: e.response?.data?.detail?.substring(0, 200) || String(e) });
    } finally { setSending(false); }
  };

  const emailLink = async (r) => {
    const ok = await confirmDialog({
      title: 'Email the signing link again?',
      description: `${r.signer_name} (${r.signer_email}) gets another email with the same link.`,
      confirmText: 'Send email',
    });
    if (!ok) return;
    try {
      const res = await losApi.resendSignLink(loan.loan_file_id, r.signature_request_id);
      if (res.email_sent) toast({ title: 'Signing link emailed', description: `Sent to ${res.sent_to}` });
      else toast({ title: 'The email did not go out', description: `${emailFailure(res.reason)} Use Copy Link instead.` });
      setRequests(await losApi.listSignatureRequests(loan.loan_file_id));
    } catch (e) {
      if (!isSpendApprovalError(e)) toast({ title: 'Send failed', description: e.response?.data?.detail?.substring(0, 200) || String(e) });
    }
  };

  const refreshRequests = async () => setRequests(await losApi.listSignatureRequests(loan.loan_file_id));
  const failToast = (e, title) => {
    if (!isSpendApprovalError(e)) toast({ title, description: e.response?.data?.detail?.substring(0, 200) || String(e) });
  };

  const turnOffLink = async (r) => {
    const ok = await confirmDialog({
      title: 'Turn off this signing link?',
      description: `${r.signer_name} will no longer be able to open or sign these forms. Make a new link to turn it back on.`,
      confirmText: 'Turn off link',
      destructive: true,
    });
    if (!ok) return;
    try {
      await losApi.revokeSignLink(loan.loan_file_id, r.signature_request_id);
      toast({ title: 'Signing link turned off' });
      await refreshRequests();
    } catch (e) { failToast(e, 'Could not turn off the link'); }
  };

  const newLink = async (r) => {
    const ok = await confirmDialog({
      title: 'Make a new signing link?',
      description: `The current link stops working at once. Nothing is emailed: use Email Link to send ${r.signer_name} the new one.`,
      confirmText: 'Make new link',
    });
    if (!ok) return;
    try {
      const res = await losApi.regenerateSignLink(loan.loan_file_id, r.signature_request_id);
      const copied = await copyText(`${window.location.origin}/e-sign/${res.signature_request.sig_token}`);
      toast({ title: 'New signing link made', description: copied ? 'Copied to your clipboard. Email Link sends it to the signer.' : 'Use Copy Link or Email Link to send it.' });
      await refreshRequests();
    } catch (e) { failToast(e, 'Could not make a new link'); }
  };

  const copySignLink = async (token) => {
    const url = `${window.location.origin}/e-sign/${token}`;
    if (await copyText(url)) toast({ title: 'Signature link copied' });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  return (
    <div className="space-y-4">
      <ModuleHeader title="Disclosure Forms & E-Sign" />
      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        {/* Left: form catalog + signer info */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] font-semibold" style={{ color: '#070B5F' }}>Forms in Package ({selected.size} selected)</div>
              <button onClick={toggleAll} className="text-[11px] font-semibold hover:underline" style={{ color: '#070B5F' }} data-testid="disclosure-toggle-all">
                {selected.size === DISCLOSURE_FORM_CATALOG.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-1.5">
              {DISCLOSURE_FORM_CATALOG.map((f) => (
                <label key={f.key} className="flex items-center gap-2 py-2 px-2 rounded hover:bg-slate-50 text-[12px] cursor-pointer" data-testid={`disclosure-form-${f.key}`}>
                  <input type="checkbox" checked={selected.has(f.key)} onChange={() => toggle(f.key)} className="accent-green-600" />
                  <FileText size={11} className="text-slate-400" />
                  <span className="truncate" style={{ color: '#070B5F' }}>{f.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="text-[13px] font-semibold mb-3" style={{ color: '#070B5F' }}>Sent Signature Requests ({requests.length})</div>
            {requests.length === 0 && <div className="text-[12px] text-slate-400 italic">None sent yet.</div>}
            <div className="space-y-2">
              {requests.map((r) => (
                <div key={r.signature_request_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100" data-testid={`sig-req-${r.signature_request_id}`}>
                  <FileCheck2 size={14} style={{ color: r.status === 'signed' ? '#6BA82E' : '#f59e0b' }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold" style={{ color: '#070B5F' }}>{r.signer_name} <span className="text-slate-400 font-normal">· {r.signer_email}</span></div>
                    <div className="text-[10px] text-slate-500">{r.forms?.length || 0} forms · Created {formatDateTime(r.created_at)} · {r.emailed_at ? `Emailed ${formatDateTime(r.emailed_at)}` : 'Not emailed'}</div>
                    {r.signed_at && <div className="text-[10px] text-emerald-600 mt-0.5">Signed {formatDateTime(r.signed_at)}{r.signer_ip ? ` · IP ${r.signer_ip}` : ''} · audit {r.audit_hash?.substring(0, 10)}…</div>}
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={r.status === 'signed' ? { background: '#dcfce7', color: '#166534' } : r.revoked_at ? { background: '#f1f5f9', color: '#475569' } : { background: '#fef3c7', color: '#b45309' }}>
                    {r.status !== 'signed' && r.revoked_at ? 'turned off' : r.status}
                  </span>
                  {r.status !== 'signed' && !r.revoked_at && (
                    <button onClick={() => emailLink(r)} className="text-[10px] font-semibold px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`email-sign-link-${r.signature_request_id}`}>Email Link</button>
                  )}
                  {!r.revoked_at && (
                    <button onClick={() => copySignLink(r.sig_token)} className="text-[10px] font-semibold px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`copy-sign-link-${r.signature_request_id}`}>Copy Link</button>
                  )}
                  {r.status !== 'signed' && (
                    <button onClick={() => newLink(r)} className="text-[10px] font-semibold px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`new-sign-link-${r.signature_request_id}`}>New Link</button>
                  )}
                  {r.status !== 'signed' && !r.revoked_at && (
                    <button onClick={() => turnOffLink(r)} className="text-[10px] font-semibold px-2 py-1 rounded border border-slate-200 text-red-700 hover:bg-red-50" data-testid={`turn-off-sign-link-${r.signature_request_id}`}>Turn Off</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: signer info + send */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 h-fit sticky top-[220px]">
          <div className="text-[13px] font-semibold mb-3" style={{ color: '#070B5F' }}>Send Package</div>
          <div className="space-y-3">
            <Fld label="Signer Name" value={signerName} onChange={setSignerName} testId="disclosure-signer-name" />
            <Fld label="Signer Email" value={signerEmail} onChange={setSignerEmail} testId="disclosure-signer-email" />
          </div>
          <button
            onClick={doSend}
            disabled={sending || selected.size === 0}
            data-testid="disclosure-send-btn"
            className="mt-4 w-full text-[13px] font-semibold px-4 py-2.5 rounded-lg text-white flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
          >
            {sending ? <Loader2 size={13} className="animate-spin" /> : <FileCheck2 size={13} />}
            {sending ? 'Building PDF…' : `Email ${selected.size} Form${selected.size !== 1 ? 's' : ''} for E-Sign`}
          </button>
          <div className="mt-3 p-3 rounded-lg text-[11px] leading-relaxed" style={{ background: 'rgba(107,168,46,0.08)', color: '#3a5717' }}>
            <b>How it works:</b> A PDF disclosure package is built with your selected forms and a unique e-sign link (14-day expiry) is emailed to the signer. If the email does not go out, copy the link and send it yourself. Each signature gets a SHA-256 audit hash.
          </div>
        </div>
      </div>
    </div>
  );
}
